import { AnyAction, AsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from './app/store';
import { Message } from './model/util/Message';

type GenericAsyncThunk = AsyncThunk<unknown, unknown, any>;

type PendingAction = ReturnType<GenericAsyncThunk['pending']>;
type FulfilledAction = ReturnType<GenericAsyncThunk['fulfilled']>;
type RejectedAction = ReturnType<GenericAsyncThunk['rejected']>;

export interface GlobalState {
  loading: boolean;
  loadingCount: number;
  loggingIn: boolean;
  loggingOut: boolean;
  message: Message | null | undefined;
}

const initialState: GlobalState = {
  loading: false,
  loadingCount: 0,
  loggingIn: false,
  loggingOut: false,
  message: null,
};

const isPendingAction = (action: AnyAction): action is PendingAction => {
  return action.type.endsWith('/pending');
};
const isFulfilledAction = (action: AnyAction): action is FulfilledAction => {
  return action.type.endsWith('/fulfilled');
};
const isRejectedAction = (action: AnyAction): action is RejectedAction => {
  return action.type.endsWith('/rejected');
};

export const setGlobalMessage = (dispatch: any, message: Message, timeoutInSeconds?: number) => {
  let timeout = 10000;
  if (timeoutInSeconds || timeoutInSeconds === 0) {
    timeout = timeoutInSeconds * 1000;
  }
  dispatch(showMessage(message));
  const rootEl = document.getElementById('root');
  rootEl?.scrollTo(0, 0);
  if (timeout !== 0) {
    setTimeout(() => {
      dispatch(hideMessage());
    }, timeout);
  }
};

export const globalSlice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setLoggingIn: (state, action) => {
      state.loggingIn = action.payload;
    },
    setLoggingOut: (state, action) => {
      state.loggingOut = action.payload;
    },
    showMessage: (state, action) => {
      state.message = action.payload;
    },
    hideMessage: (state) => {
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(isPendingAction, (state) => {
        state.loadingCount += 1;
      })
      .addMatcher(isRejectedAction, (state) => {
        state.loadingCount -= 1;
      })
      .addMatcher(isFulfilledAction, (state) => {
        state.loadingCount -= 1;
      });
  },
});

export const { setLoading, setLoggingIn, setLoggingOut, showMessage, hideMessage } =
  globalSlice.actions;

export const selectLoading = (state: RootState) => state.global.loading;
export const selectLoadingCount = (state: RootState) => state.global.loadingCount;
export const selectLoggingIn = (state: RootState) => state.global.loggingIn;
export const selectLoggingOut = (state: RootState) => state.global.loggingOut;
export const selectMessage = (state: RootState) => state.global.message;

export default globalSlice.reducer;
